import { useCallback, useMemo } from 'react';
import {
  enrichFormsWidgetSections,
  findFormsWidgetFormIds,
  toActiveFormOptions,
} from './formsWidgetData.util';
import { useFormsWidgetData } from './useFormsWidgetData';
import { useFormsWidgetMutations } from './useFormsWidgetMutations';

/**
 * Single entry point for the plan detail hook: loads Forms data, exposes the
 * render-time enrichment, edit handlers, and Add Forms modal props. The modal lists
 * active forms not yet attached to the target widget.
 *
 * The detail hook centrally gates edit handlers by section-write access. This hook
 * applies the Forms-specific read permission so edits never reveal hidden form data.
 *
 * @param {{
 *   casePlanId: string,
 *   sections: object[],
 *   setSections: Function,
 *   personId: number | null,
 *   canReadForms: boolean,
 * }} params
 */
export const useFormsWidget = ({ casePlanId, sections, setSections, personId, canReadForms }) => {
  const { catalog, submissionsByWidgetId, refreshCatalog, exportResponse } = useFormsWidgetData({
    sections,
    personId,
    canReadForms,
  });

  const mutations = useFormsWidgetMutations({
    casePlanId,
    sections,
    setSections,
    onOpenCatalog: refreshCatalog,
  });

  const enrichSections = useCallback(
    (sectionsToRender) =>
      enrichFormsWidgetSections(sectionsToRender, {
        canReadForms,
        catalog,
        submissionsByWidgetId,
        onResponseClick: exportResponse,
      }),
    [canReadForms, catalog, submissionsByWidgetId, exportResponse],
  );

  const editHandlers = canReadForms ? mutations : undefined;

  const attachedFormIds = findFormsWidgetFormIds(sections, mutations.targetWidgetId);

  const addFormsModal = useMemo(
    () => ({
      ...mutations.addFormsModal,
      forms: toActiveFormOptions(catalog.rows, attachedFormIds),
      loading: catalog.loading,
      error: catalog.error,
    }),
    [mutations.addFormsModal, catalog, attachedFormIds],
  );

  return {
    enrichSections,
    editHandlers,
    addFormsModal,
  };
};
